<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { DateTime } from 'luxon'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import LocaleLink from '@/components/base/LocaleLink.vue'
import { LOADING_SIZES, LOADING_WIDTH, uuid } from '@/composables'
import { handleAxiosError } from '@/composables/useAxiosError'
import { acceptTerms, useTermsSummaries } from '@/composables/useTerms'
import {
  formatVersionDate,
  hasSnapshot,
  isPlaceholder,
  loadSnapshot,
  reloadOnceFor,
  versionsBetween,
  type FlatBundle
} from '@/legal/termsBundle'
import { interpolateMessage } from '@/legal/termsDocument'
import { useNotifyStore, useTermsAcceptance, useTermsStore } from '@/stores'

const { t, locale } = useI18n()
const notifyStore = useNotifyStore()
const termsStore = useTermsStore()
const { mayAccept, prompt: offerAcceptance, avvPrompt } = useTermsAcceptance()
const summariesFor = useTermsSummaries()

const loading = ref(true)
const loadFailed = ref(false)
const accepting = ref(false)
const declared = ref(false)
const notice = ref<string | null>(null)
const bundle = ref<FlatBundle | null>(null)

const status = computed(() => termsStore.status)
const uiLocale = computed(() => (locale.value === 'en' ? 'en' : 'de'))

// The version the page shows and posts: Current as the published list reports it.
const version = computed(() => termsStore.config?.current_version ?? status.value?.current_version ?? null)
const governing = computed(() => status.value?.governing_version ?? null)
const versionEntry = computed(() => termsStore.config?.versions.find((v) => v.id === version.value))

const dateOf = (id: string) => formatVersionDate(id, uiLocale.value)
const formatDate = (iso: string) => DateTime.fromISO(iso).setLocale(uiLocale.value).toLocaleString(DateTime.DATE_FULL)

const textUnavailable = ref(false)
const declaration = computed(() => {
  const flat = bundle.value
  if (!flat || !version.value) return null
  const pick = (key: string) => {
    const raw = flat[`termsPage.acceptance.${key}`]
    return raw && !isPlaceholder(raw) ? raw : null
  }
  const intro = governing.value ? pick('intro') : pick('introWithoutOldVersion')
  const checkbox = pick('checkbox')
  const button = pick('button')
  if (!intro || !checkbox || !button) return null
  const effective = versionEntry.value?.effective_at ?? status.value?.effective_at
  const params: Record<string, string> = {
    date: dateOf(version.value),
    ...(governing.value ? { oldDate: dateOf(governing.value) } : {}),
    ...(effective ? { effectiveDate: formatDate(effective) } : {})
  }
  const format = (raw: string) => interpolateMessage(raw, (name) => params[name])
  return { intro: format(intro), checkbox: format(checkbox), button: format(button) }
})

const canCompare = computed(() => !!governing.value && !!version.value && hasSnapshot(governing.value))
const summaries = computed(() =>
  version.value ? summariesFor(versionsBetween(termsStore.config, governing.value, version.value)) : []
)

// Text the bundle does not carry is not shown or posted: reload once onto the build that does.
const loadText = async () => {
  bundle.value = null
  textUnavailable.value = false
  if (!offerAcceptance.value || !version.value) return
  if (!hasSnapshot(version.value)) {
    if (!reloadOnceFor(version.value)) textUnavailable.value = true
    return
  }
  const snapshot = await loadSnapshot(version.value)
  bundle.value = snapshot?.[uiLocale.value] ?? null
  if (!declaration.value) textUnavailable.value = true
}

const load = async () => {
  loading.value = true
  loadFailed.value = false
  await Promise.all([termsStore.loadStatus(true), termsStore.loadConfig(true)])
  if (mayAccept.value) await termsStore.loadAvvStatus(true)
  if (!status.value) loadFailed.value = true
  else await loadText()
  loading.value = false
}

const REFUSALS: Record<string, string> = {
  email_not_verified: 'dashboard.account.terms.emailNotVerified',
  account_inactive: 'dashboard.account.terms.accountInactive'
}

const accept = async () => {
  if (accepting.value || !declared.value || !version.value || !declaration.value) return
  accepting.value = true
  notice.value = null
  const shown = version.value
  try {
    const result = await acceptTerms(shown)
    if (result.kind === 'accepted') {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.account.terms.acceptedTitle'),
        message: t('dashboard.account.terms.acceptedMessage', { date: dateOf(shown) })
      })
      declared.value = false
      if (result.status) termsStore.setStatus(result.status)
      else await termsStore.loadStatus(true)
      bundle.value = null
    } else if (result.kind === 'stale') {
      declared.value = false
      notice.value = t('dashboard.account.terms.stale')
      await load()
    } else {
      notice.value = t(REFUSALS[result.code ?? ''] ?? 'dashboard.account.terms.holderOnly')
    }
  } catch (err) {
    handleAxiosError(err, { logPrefix: 'AccountPageTerms:accept' })
    notice.value = t('dashboard.account.terms.error.acceptFailed')
  } finally {
    accepting.value = false
  }
}

const reload = () => window.location.reload()
const savePdf = () => window.print()

onMounted(load)
</script>

<template>
  <BaseDashboardPage :title="$t('dashboard.account.terms.title')" icon="mdi-file-document-check-outline" icon-color="cyan">
    <div class="terms-page">
      <div v-if="loading" class="dashboard-loading-state">
        <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
        <p>{{ $t('dashboard.account.terms.loading') }}</p>
      </div>

      <div v-else-if="loadFailed" class="state-box" data-test="terms-load-failed">
        <p class="dashboard-alert dashboard-alert--error">{{ $t('dashboard.account.terms.error.loadFailed') }}</p>
        <button type="button" class="dashboard-action-btn dashboard-action-btn--secondary" @click="load">
          <v-icon icon="mdi-refresh" size="18" />
          <span>{{ $t('dashboard.account.terms.error.retry') }}</span>
        </button>
      </div>

      <template v-else-if="status && version">
        <p v-if="notice" class="dashboard-alert dashboard-alert--warning" data-test="terms-notice">{{ notice }}</p>

        <div class="terms-facts">
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.account.terms.currentVersion', { date: dateOf(version) }) }}</span>
            <div class="kv-value">
              <LocaleLink to="termsVersion" :params="{ version }" target="_blank" class="terms-link">
                {{ $t('dashboard.account.terms.fullText') }}
              </LocaleLink>
              <LocaleLink
                v-if="canCompare && status.needs_acceptance"
                to="termsCompare"
                :params="{ from: governing!, to: version }"
                target="_blank"
                class="terms-link"
                data-test="terms-compare-link"
              >
                {{ $t('dashboard.account.terms.comparison') }}
              </LocaleLink>
            </div>
          </div>
          <div v-if="status.needs_acceptance && governing" class="kv-row">
            <div class="kv-wide">{{ $t('dashboard.account.terms.governingVersion', { date: dateOf(governing) }) }}</div>
          </div>
          <div v-if="status.accepted_version && status.accepted_at" class="kv-row" data-test="terms-accepted">
            <div class="kv-wide">
              {{
                $t('dashboard.account.terms.acceptedInfo', {
                  version: dateOf(status.accepted_version),
                  date: formatDate(status.accepted_at)
                })
              }}
            </div>
          </div>
        </div>

        <div v-if="offerAcceptance" class="terms-print-hide">
          <button type="button" class="dashboard-action-btn dashboard-action-btn--secondary" data-test="terms-save-pdf" @click="savePdf">
            <v-icon icon="mdi-file-pdf-box" size="18" />
            <span>{{ $t('dashboard.account.terms.savePdf') }}</span>
          </button>
        </div>

        <p v-if="!status.needs_acceptance && !avvPrompt" class="dashboard-alert dashboard-alert--success" data-test="terms-nothing-pending">
          {{ $t('dashboard.account.terms.nothingPending') }}
        </p>

        <p v-else-if="!mayAccept" class="dashboard-alert dashboard-alert--info" data-test="terms-holder-only">
          {{ $t('dashboard.account.terms.holderOnly') }}
        </p>

        <template v-else-if="status.needs_acceptance">
          <section v-if="summaries.length" class="terms-summary" data-test="terms-summary">
            <h3 class="section-title">{{ $t('dashboard.account.terms.summaryTitle') }}</h3>
            <ol v-for="summary in summaries" :key="summary.id">
              <li v-for="(point, i) in summary.points" :key="i">{{ point }}</li>
            </ol>
          </section>

          <div v-if="textUnavailable" class="state-box" data-test="terms-text-unavailable">
            <p class="dashboard-alert dashboard-alert--warning">{{ $t('dashboard.account.terms.textUnavailable') }}</p>
            <button type="button" class="dashboard-action-btn dashboard-action-btn--secondary" @click="reload">
              <v-icon icon="mdi-refresh" size="18" />
              <span>{{ $t('dashboard.account.terms.reload') }}</span>
            </button>
          </div>

          <form v-else-if="declaration" class="terms-declaration" data-test="terms-declaration" @submit.prevent="accept">
            <p class="declaration-intro">{{ declaration.intro }}</p>
            <label class="checkbox-label">
              <input v-model="declared" type="checkbox" data-test="terms-checkbox" />
              <span>{{ declaration.checkbox }}</span>
            </label>
            <div class="declaration-actions">
              <button
                type="submit"
                class="dashboard-action-btn dashboard-action-btn--primary"
                data-test="terms-accept"
                :disabled="!declared || accepting"
              >
                <v-progress-circular v-if="accepting" indeterminate :size="LOADING_SIZES.button" :width="LOADING_WIDTH" />
                <template v-else>
                  <v-icon icon="mdi-check" size="18" />
                  <span>{{ declaration.button }}</span>
                </template>
              </button>
            </div>
          </form>
        </template>

        <section v-if="avvPrompt" class="terms-avv" data-test="terms-avv">
          <h3 class="section-title">{{ $t('dashboard.account.terms.avv.title') }}</h3>
          <p>{{ $t('dashboard.account.terms.avv.text') }}</p>
          <router-link :to="{ name: 'dashboardAvv' }" class="dashboard-action-btn dashboard-action-btn--primary" data-test="terms-avv-link">
            <v-icon icon="mdi-file-sign" size="18" />
            <span>{{ $t('dashboard.account.terms.avv.link') }}</span>
          </router-link>
        </section>
      </template>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.terms-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 24px;

  .dashboard-alert {
    margin-bottom: 0;
  }
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.terms-facts {
  margin: 0;
}

.kv-row {
  display: grid;
  grid-template-columns: minmax(200px, auto) 1fr;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }

  .kv-value,
  .kv-wide {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.9rem;
  }

  .kv-wide {
    grid-column: 1 / -1;
  }
}

.kv-label {
  color: rgba(200, 218, 255, 0.5);
  font-size: 0.85rem;
}

.terms-link {
  color: #4dd0e1;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 8px;
}

.terms-summary {
  background: rgba(0, 176, 255, 0.04);
  border: 1px solid rgba(0, 176, 255, 0.15);
  border-radius: 10px;
  padding: 16px;

  ol {
    margin: 0 0 8px;
    padding-left: 1.5em;
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.9rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  li {
    margin-bottom: 6px;
  }
}

.terms-avv {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid rgba(120, 163, 255, 0.08);

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.9rem;
  }
}

.terms-declaration {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(120, 163, 255, 0.08);
}

.declaration-intro {
  margin: 0;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9rem;
  line-height: 1.6;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  line-height: 1.5;

  input {
    margin-top: 4px;
    flex-shrink: 0;
    accent-color: #00b0ff;
  }
}

.declaration-actions {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 600px) {
  .terms-page {
    padding: 16px;
  }

  .kv-row {
    grid-template-columns: 1fr;
  }

  .declaration-actions .dashboard-action-btn {
    width: 100%;
  }
}
</style>

<style lang="scss">
/* Unscoped: the dashboard shell sits outside this component. Prints the page without the shell on white. */
@media print {
  body:has(.terms-page) {
    .dashboard-layout {
      background: #fff;
    }

    .bg-decoration,
    .dashboard-wrapper > :not(.dashboard-main),
    .dashboard-main > :not(.dashboard-content),
    .terms-print-hide {
      display: none !important;
    }

    .dashboard-main {
      margin-left: 0;
      padding: 0;
    }

    .dashboard-content * {
      color: #000 !important;
      background: none !important;
      border-color: #000 !important;
    }
  }
}
</style>
